// Renewal Board panel — FE-SCRM-008 (Grid rendering core) and
// FE-PINV-063 (shell + 3-view router).
//
// Owns the URL-driven filter state, fetches the grid, renders the
// new toolbar slot + view-toggle pills + view body (Grid today;
// Kanban and List slot stubs until W3/W4 land their view bodies in
// the redesign round). Drag-persistence and the count-tile cell
// modal stay wired against Grid.
//
// FE-PINV-063 deletes the v1 Timeline rendering + Timeline toggle,
// the sidebar filter panel, and the preset chip row. The replacement
// surfaces (FE-PINV-064 horizontal toolbar / FE-PINV-065 saved-view
// switcher / FE-PINV-068+ Grid axis updates / FE-PINV-070 Kanban /
// FE-PINV-071 List) land in W2/W3/W4 of the redesign round.

import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { Button, Modal } from "@/design-system";

import { useTeamMembersQuery } from "../AddOpportunity/hooks";
import { ApplyPlaybookModal } from "../Playbook/ApplyPlaybookModal";
import type { TargetSearchResult } from "../components/TargetSearchPicker";
import { fetchGlobalSearch } from "../search/api";

import {
  BULK_MOVE_STAGE_OPTIONS_FORWARD,
  BulkActionBar,
} from "../pipeline/BulkActionBar";
import {
  archiveOpportunity,
  useBulkMoveStageMutation,
} from "../pipeline/bulkActions";
import {
  EMPTY_PIPELINE_FILTERS,
  type Density,
  type PipelineFilters,
} from "../pipeline/filterState";
import { NewChooser } from "../pipeline/NewChooser";
import { PipelineListView } from "../pipeline/PipelineListView";
import { PipelineToolbar } from "../pipeline/PipelineToolbar";
import { SavedViewSwitcher } from "../pipeline/SavedViewSwitcher";
import {
  useCreateSavedViewMutation, useSavedViewsQuery,
} from "../pipeline/savedViewHooks";
import { useListSelection } from "../pipeline/useListSelection";

import { CellModal } from "./CellModal";
import { Grid } from "./Grid";
import { Kanban } from "./Kanban";
import { MyBookKpiStrip } from "./MyBookKpiStrip";
import {
  computeGridStartMonth,
  readBoardStateFromUrl,
  writeBoardStateToParams,
  type GridMonthSpan,
  type PipelineView,
} from "./filterState";
import {
  useChangeRenewalMonthMutation,
  useMoveOpportunityStageMutation,
  useRenewalBoardCellQuery,
  useRenewalBoardQuery,
} from "./hooks";
import type { BoardFilters } from "./types";


type SortKey =
  | "cadence_health_severity"
  | "days_until_renewal"
  | "value_estimate"
  | "prospect_name";


const _VIEW_LABEL: Record<PipelineView, string> = {
  grid: "Grid",
  kanban: "Kanban",
  list: "List",
};


// Adapters bridging legacy BoardFilters (the API-bound shape consumed
// by the Renewal Board query) and W2's PipelineFilters (the toolbar's
// shape). Five facets overlap with identical enum vocabularies; stage
// + renewal_window are toolbar-only and ride through pipelineFilters
// but degrade to no-op against the board query in v1.
function boardToPipelineFilters(b: BoardFilters): PipelineFilters {
  return {
    cadence: b.cadence_health,
    stage: [],
    lives_band: b.lives_band,
    renewal_window: null,
    opportunity_type: b.opportunity_type,
    producer: b.producer,
    alerts_only: b.has_active_alerts === true,
  };
}


function pipelineToBoardFilters(
  p: PipelineFilters, prev: BoardFilters,
): BoardFilters {
  return {
    ...prev,
    producer: p.producer,
    lives_band: p.lives_band,
    opportunity_type: p.opportunity_type,
    cadence_health: p.cadence,
    has_active_alerts: p.alerts_only ? true : null,
  };
}


export function RenewalBoardPanel() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();

  // Parse the URL-driven state on every render. The setter writes
  // back via setSearch so the URL stays canonical.
  const urlState = useMemo(
    () => readBoardStateFromUrl(search), [search],
  );

  // Cell modal state (which tile is open + secondary filters).
  const [cell_target, setCellTarget] = useState<
    { month: string; stage: string } | null
  >(null);
  const [cell_sort, setCellSort] = useState<SortKey>(
    "cadence_health_severity",
  );
  const [cell_min_lives, setCellMinLives] = useState<number | null>(null);

  // Toolbar-local density preference. List-view-specific persistence
  // rides through SavedView.columns_json (FE-PINV-073); Grid + Kanban
  // ignore density today. Local state until a v1.5 chunk lifts it.
  const [density, setDensity] = useState<Density>("comfortable");

  // FE-PINV-071 / FE-PINV-072 — List view selection state. Hoisted
  // to the panel so the bulk-action bar (FE-PINV-072) can mount as a
  // sibling and share state across sort + density toggles.
  const list_selection = useListSelection();
  const bulk_move_stage = useBulkMoveStageMutation();
  const create_saved_view = useCreateSavedViewMutation();
  const [apply_playbook_open, setApplyPlaybookOpen] = useState(false);

  // Drag-to-renewal-month confirmation modal state.
  const [pending_renewal_move, setPendingRenewalMove] = useState<{
    opportunity_id: string;
    from: { renewal_year: number; renewal_month: number };
    to: { renewal_year: number; renewal_month: number };
  } | null>(null);

  // FE-PINV-068 — Grid axis window. Default 6 months starting at the
  // current calendar month; paddles bump grid_start_offset by ±1
  // month and "Show 12 months" expands grid_month_span. URL keys
  // ``gridSpan`` + ``gridOffset`` round-trip per the codec.
  const grid_window = useMemo(() => ({
    start_month: computeGridStartMonth(urlState.grid_start_offset),
    num_months: urlState.grid_month_span,
  }), [urlState.grid_start_offset, urlState.grid_month_span]);

  // ── Data queries ──────────────────────────────────────────────
  const board_query = useRenewalBoardQuery(grid_window, urlState.filters);
  const cell_query = useRenewalBoardCellQuery(
    cell_target
      ? {
          month: cell_target.month,
          stage: cell_target.stage,
          filters: urlState.filters,
          sort: cell_sort,
          min_lives: cell_min_lives ?? undefined,
        }
      : null,
  );

  const move_stage = useMoveOpportunityStageMutation();
  const change_renewal = useChangeRenewalMonthMutation();

  // ── State updaters ───────────────────────────────────────────
  function updateState(
    next: Partial<{
      filters: BoardFilters;
      preset_id: string | null;
      view: PipelineView;
      show_closed: boolean;
      grid_month_span: GridMonthSpan;
      grid_start_offset: number;
    }>,
  ) {
    const merged = {
      filters: next.filters ?? urlState.filters,
      preset_id: next.preset_id === undefined
        ? urlState.preset_id
        : next.preset_id,
      view: next.view ?? urlState.view,
      show_closed: next.show_closed === undefined
        ? urlState.show_closed
        : next.show_closed,
      grid_month_span: next.grid_month_span ?? urlState.grid_month_span,
      grid_start_offset: next.grid_start_offset === undefined
        ? urlState.grid_start_offset
        : next.grid_start_offset,
    };
    setSearch(writeBoardStateToParams(merged, search), { replace: true });
  }

  // ── Drag handlers ────────────────────────────────────────────
  function handleDrop(params: {
    opportunity_id: string;
    from: { stage: string; renewal_year: number; renewal_month: number };
    to: { stage: string; renewal_year: number; renewal_month: number };
  }) {
    const stage_changed = params.from.stage !== params.to.stage;
    const month_changed = (
      params.from.renewal_year !== params.to.renewal_year
      || params.from.renewal_month !== params.to.renewal_month
    );
    if (stage_changed && !month_changed) {
      move_stage.mutate({
        opportunity_id: params.opportunity_id,
        to_stage: params.to.stage,
      });
      return;
    }
    if (month_changed) {
      setPendingRenewalMove({
        opportunity_id: params.opportunity_id,
        from: {
          renewal_year: params.from.renewal_year,
          renewal_month: params.from.renewal_month,
        },
        to: {
          renewal_year: params.to.renewal_year,
          renewal_month: params.to.renewal_month,
        },
      });
      if (stage_changed) {
        move_stage.mutate({
          opportunity_id: params.opportunity_id,
          to_stage: params.to.stage,
        });
      }
    }
  }

  function confirmRenewalMove() {
    if (!pending_renewal_move) return;
    change_renewal.mutate({
      opportunity_id: pending_renewal_move.opportunity_id,
      renewal_month: pending_renewal_move.to.renewal_month,
      renewal_year: pending_renewal_move.to.renewal_year,
    });
    setPendingRenewalMove(null);
  }

  // ── Toolbar wiring ───────────────────────────────────────────
  //
  // PipelineToolbar carries the W2 PipelineFilters shape (cadence /
  // stage / lives_band / renewal_window / opportunity_type / producer
  // / alerts_only). The Renewal Board query takes the legacy
  // BoardFilters shape (cadence_health / producer / broker_location /
  // lives_band / industry / tags / opportunity_type /
  // renewal_confidence / has_active_alerts). Overlapping fields ride
  // through; stage + renewal_window are toolbar-only inputs that the
  // BE board query does not accept in v1 (filter degrades to no-op).
  const pipelineFilters = useMemo<PipelineFilters>(
    () => boardToPipelineFilters(urlState.filters),
    [urlState.filters],
  );

  // FE-SCRM-038 — Producer landing page KPI strip is gated on the
  // active saved view being the pinned-default ("My book"). Read
  // the saved-views list to find the pinned default, then check if
  // its id matches the active preset; null preset (first mount,
  // before SavedViewSwitcher hydrates) also counts as My-book.
  const saved_views_q = useSavedViewsQuery();
  const my_book_active = useMemo(() => {
    const rows = saved_views_q.data?.rows ?? [];
    const pinned = rows.find((r) => r.is_pinned_default);
    if (!pinned) return false;
    return (
      urlState.preset_id === null
      || urlState.preset_id === pinned.id
    );
  }, [saved_views_q.data, urlState.preset_id]);

  const team_members_q = useTeamMembersQuery();
  const producer_options = useMemo(
    () =>
      (team_members_q.data?.rows ?? [])
        .filter((m) => m.is_producer)
        .map((m) => ({
          id: m.member_id,
          label: `${m.first_name} ${m.last_name}`.trim() || m.email,
        })),
    [team_members_q.data],
  );
  // FE-SCRM-064b: Producer cell on the List view resolves
  // ``row.producer_id`` (a Membership UUID) to a display name via
  // this map; without it the cell renders the raw UUID. Covers
  // every team member (not just producers) so a producer who lost
  // their ``is_producer`` flag still resolves to a name.
  const producer_labels = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of team_members_q.data?.rows ?? []) {
      const label = `${row.first_name} ${row.last_name}`.trim()
        || row.email;
      m.set(row.member_id, label);
    }
    return m;
  }, [team_members_q.data]);

  // NewChooser fetch adapter — restricts the global-search response
  // to the opportunity bucket and projects into TargetSearchResult.
  const fetchOppSearch = useCallback(
    async (q: string): Promise<TargetSearchResult[]> => {
      if (!q.trim()) return [];
      const res = await fetchGlobalSearch(q, { limit: 10 });
      return res.opportunities.map((row) => ({
        id: row.id,
        kind: "opportunity" as const,
        label: row.label,
        sublabel: row.sublabel ?? undefined,
        parent_prospect_id: row.prospect_id ?? undefined,
      }));
    },
    [],
  );

  // ── Render ───────────────────────────────────────────────────
  return (
    <div data-testid="renewal-board-panel" className="flex flex-col gap-2">
      <MyBookKpiStrip
        visible={my_book_active}
        board={board_query.data ?? null}
      />
      {/* Single-row toolbar: filter controls on the left, view +
          grid-axis controls on the right. Previously split across
          two rows (toolbar + GridChrome) plus a two-line page
          header; that stack consumed ~180px above the grid before
          the board could draw. Collapsed here so the producer gets
          the maximum scroll viewport for the cards. */}
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SavedViewSwitcher
            activeViewId={urlState.preset_id}
            filters={pipelineFilters}
            onSelect={(view) =>
              updateState({
                preset_id: view.id,
                filters: pipelineToBoardFilters(
                  view.filters_json, urlState.filters,
                ),
              })
            }
          />
          <PipelineToolbar
            filters={pipelineFilters}
            onChange={(next) =>
              updateState({
                preset_id: null,
                filters: pipelineToBoardFilters(next, urlState.filters),
              })
            }
            showClosed={urlState.show_closed}
            onShowClosedChange={(v) => updateState({ show_closed: v })}
            density={density}
            onDensityChange={setDensity}
            producerOptions={producer_options}
            // FE-SCRM-064 (c): density toggle is List-view-only.
            showDensity={urlState.view === "list"}
            // FE-SCRM-064 (d): closed_won + closed_lost summary count
            // from BE-SCRM-007 — renders as a chip badge on Show
            // closed so the producer knows how many opps the toggle
            // would reveal.
            closedCount={
              (board_query.data?.summary_row.closed_won.count ?? 0)
              + (board_query.data?.summary_row.closed_lost.count ?? 0)
            }
          />
        </div>
        <div className="flex items-center gap-3">
          <NewChooser fetchOpportunitySearchResults={fetchOppSearch} />
          <PipelineViewToggle
            view={urlState.view}
            on_change={(v) => updateState({ view: v })}
          />
          {urlState.view === "grid" && (
            <GridChrome
              month_span={urlState.grid_month_span}
              on_paddle={(delta) =>
                updateState({
                  grid_start_offset: urlState.grid_start_offset + delta,
                })
              }
              on_span_toggle={() =>
                updateState({
                  grid_month_span: urlState.grid_month_span === 12 ? 6 : 12,
                })
              }
            />
          )}
        </div>
      </header>

      <main className="min-w-0">
        <PipelineViewBody view={urlState.view}>
          {board_query.isLoading ? (
            <DashboardLoadingState variant="card" />
          ) : board_query.isError ? (
            <DashboardErrorState
              title="Couldn't load the Renewal Board"
              description="Refresh to retry, or check the network connection."
              retry={() => board_query.refetch()}
            />
          ) : board_query.data && isEmpty(board_query.data) ? (
            <DashboardEmptyState
              title="Get started by importing your book of business"
              description={
                "The Renewal Board lays your full pipeline across the "
                + "next 12 months by stage. Once you import prospects "
                + "or add an opportunity, this is where renewals land."
              }
              action={
                <Link
                  data-testid="renewal-board-empty-import-cta"
                  to="/dashboard/sales-crm/import?type=prospects"
                  className={[
                    "inline-flex items-center justify-center rounded-md",
                    "bg-teal px-4 py-2 text-sm font-medium",
                    "text-text-inverse hover:opacity-90",
                  ].join(" ")}
                >
                  Import your book of business
                </Link>
              }
            />
          ) : board_query.data && urlState.view === "grid" ? (
            <Grid
              board={board_query.data}
              onOpportunityClick={(id) =>
                navigate(`/dashboard/sales-crm/opportunities/${id}`)
              }
              onCountTileClick={(target) => {
                setCellTarget(target);
                setCellSort("cadence_health_severity");
                setCellMinLives(null);
              }}
              onDrop={handleDrop}
            />
          ) : board_query.data && urlState.view === "kanban" ? (
            <Kanban
              board={board_query.data}
              show_closed={urlState.show_closed}
              onDrop={({ opportunity_id, to_stage }) =>
                move_stage.mutate({ opportunity_id, to_stage })
              }
            />
          ) : urlState.view === "list" ? (
            <>
              <PipelineListView
                board={board_query.data}
                showClosed={urlState.show_closed}
                selection={list_selection}
                producerLabels={producer_labels}
                onOpenFilters={() => undefined}
                onSaveAsView={(cols) => {
                  // FE-PINV-073 — "Save as view" routes the canonical
                  // columns_json + density into a SavedView. The
                  // <SavedViewSwitcher> owns the rename + pin UX; this
                  // fire-and-forget save creates the row so the cols
                  // round-trip; renaming is done from the switcher.
                  create_saved_view.mutate({
                    name: `Custom columns (${new Date()
                      .toISOString().slice(0, 10)})`,
                    filters_json: EMPTY_PIPELINE_FILTERS,
                    columns_json: {
                      visible: cols.visible,
                      order: cols.order,
                      density: cols.density,
                    },
                  });
                }}
              />
              <BulkActionBar
                selection={list_selection}
                moveStageOptions={BULK_MOVE_STAGE_OPTIONS_FORWARD}
                isPending={bulk_move_stage.isPending}
                onMoveStage={(target_stage) => {
                  bulk_move_stage.mutate(
                    {
                      opportunity_ids: Array.from(list_selection.selected),
                      target_stage,
                    },
                    { onSuccess: () => list_selection.clear() },
                  );
                }}
                onAssignProducer={() => {
                  // v1.5: bulk producer assignment goes through a
                  // host-owned picker. Producer-bulk surface lives in
                  // the next milestone (FE-PINV-072.5) — leave this
                  // callback wired but inert so the surface contract
                  // stays addressable from FE.
                }}
                onApplyPlaybook={() => setApplyPlaybookOpen(true)}
                onAddToList={() => {
                  // v1.5: routes through W2's <SavedViewSwitcher>
                  // save flow. The handle for that mount lives in W2;
                  // surface the host hook here so a future commit can
                  // open the save-as-new modal pre-populated with the
                  // augmented filter.
                }}
                onBulkLogTouchpoint={() => {
                  // v1.5: opens W5's <ActivityComposerModal> in bulk
                  // mode. Wired in the FE-SCRM-023 follow-up; this
                  // host callback is the stable contract bulk-action
                  // tests already exercise.
                }}
                onArchive={async () => {
                  const ids = Array.from(list_selection.selected);
                  // Sequential fan-out keeps the per-row error
                  // narrative; the bulk endpoint is move-stage-only.
                  for (const id of ids) {
                    try {
                      await archiveOpportunity(id);
                    } catch {
                      // ignore — per-row failures surface in a v1.5
                      // toast pass; the FE bar already collapses on
                      // success and stays open on partial failure so
                      // the producer can retry.
                    }
                  }
                  list_selection.clear();
                }}
              />
            </>
          ) : null}
        </PipelineViewBody>
      </main>

      {cell_target && (
        <CellModal
          target={cell_target}
          data={cell_query.data}
          is_loading={cell_query.isLoading}
          on_close={() => setCellTarget(null)}
          on_opportunity_click={(id) => {
            setCellTarget(null);
            navigate(`/dashboard/sales-crm/opportunities/${id}`);
          }}
          sort={cell_sort}
          on_sort_change={setCellSort}
          min_lives={cell_min_lives}
          on_min_lives_change={setCellMinLives}
        />
      )}

      {apply_playbook_open ? (
        <ApplyPlaybookModal
          mode="bulk"
          opportunityIds={Array.from(list_selection.selected)}
          onClose={() => setApplyPlaybookOpen(false)}
          onSuccess={() => {
            setApplyPlaybookOpen(false);
            list_selection.clear();
          }}
        />
      ) : null}

      {pending_renewal_move && (
        <RenewalConfirmModal
          from={pending_renewal_move.from}
          to={pending_renewal_move.to}
          on_confirm={confirmRenewalMove}
          on_cancel={() => setPendingRenewalMove(null)}
        />
      )}
    </div>
  );
}


function isEmpty(board: { columns: { cells: { count: number }[] }[]; summary_row: { closed_won: { count: number }; closed_lost: { count: number } } }): boolean {
  const grid = board.columns.every((col) =>
    col.cells.every((c) => c.count === 0),
  );
  const summary = (
    board.summary_row.closed_won.count === 0
    && board.summary_row.closed_lost.count === 0
  );
  return grid && summary;
}


// FE-PINV-063: 3-way view toggle with aria-pressed per button.
// Mirrors FE-SCRM-008's prior geometry-toggle pattern so the
// aria-pressed convention doesn't collide with the outer
// SalesCrmPage tab list.
function PipelineViewToggle({
  view, on_change,
}: {
  view: PipelineView;
  on_change: (v: PipelineView) => void;
}) {
  return (
    <div
      data-testid="pipeline-view-toggle"
      role="group"
      aria-label="Pipeline view"
      className="flex items-center gap-1 bg-canvas rounded p-0.5"
    >
      {(["grid", "kanban", "list"] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={view === v}
          data-testid={`pipeline-view-${v}`}
          onClick={() => on_change(v)}
          className={[
            "px-2 py-1 text-xs rounded",
            view === v
              ? "bg-raised text-text-primary shadow-sm"
              : "text-text-tertiary hover:text-text-primary",
          ].join(" ")}
        >
          {_VIEW_LABEL[v]}
        </button>
      ))}
    </div>
  );
}


// FE-PINV-063: view-body slot. Renders the active view's container
// with a stable data-testid downstream chunks (W3 / W4) target.
// Today only "grid" renders real content; "kanban" and "list" are
// placeholder slots until FE-PINV-070 / FE-PINV-071 fill them in.
function PipelineViewBody({
  view, children,
}: {
  view: PipelineView;
  children: React.ReactNode;
}) {
  if (view === "grid") {
    return (
      <div data-testid="pipeline-view-body-grid">
        {children}
      </div>
    );
  }
  if (view === "kanban") {
    return (
      <div data-testid="pipeline-view-body-kanban">
        {children}
      </div>
    );
  }
  return (
    <div data-testid="pipeline-view-body-list" className="min-w-0">
      {children}
    </div>
  );
}


function MONTH_LABEL(year: number, month: number): string {
  const names = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${names[month - 1] ?? month} ${year}`;
}


// FE-PINV-068: header row that sits between the toolbar/view toggle
// and the Grid body. Renders the < > paddles for moving the visible
// window by one month and the "Show 12 months" / "Show 6 months"
// toggle. View-gated; mounts only when ``view === "grid"``.
function GridChrome({
  month_span, on_paddle, on_span_toggle,
}: {
  month_span: GridMonthSpan;
  on_paddle: (delta: number) => void;
  on_span_toggle: () => void;
}) {
  return (
    <div
      data-testid="grid-chrome"
      className="flex items-center justify-end gap-2"
    >
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          data-testid="grid-paddle-prev"
          aria-label="Previous month"
          onClick={() => on_paddle(-1)}
          className={[
            "inline-flex h-6 w-6 items-center justify-center",
            "rounded text-text-tertiary hover:bg-recessed",
            "hover:text-text-primary",
          ].join(" ")}
        >
          ‹
        </button>
        <button
          type="button"
          data-testid="grid-paddle-next"
          aria-label="Next month"
          onClick={() => on_paddle(1)}
          className={[
            "inline-flex h-6 w-6 items-center justify-center",
            "rounded text-text-tertiary hover:bg-recessed",
            "hover:text-text-primary",
          ].join(" ")}
        >
          ›
        </button>
      </div>
      <button
        type="button"
        data-testid="grid-span-toggle"
        onClick={on_span_toggle}
        className={[
          "rounded-md px-2 py-1 text-xs",
          "text-text-secondary hover:bg-recessed hover:text-text-primary",
        ].join(" ")}
      >
        {month_span === 12 ? "Show 6 months" : "Show 12 months"}
      </button>
    </div>
  );
}


function RenewalConfirmModal({
  from, to, on_confirm, on_cancel,
}: {
  from: { renewal_year: number; renewal_month: number };
  to: { renewal_year: number; renewal_month: number };
  on_confirm: () => void;
  on_cancel: () => void;
}) {
  return (
    <Modal
      open
      onClose={on_cancel}
      title="Change renewal month?"
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            data-testid="renewal-confirm-cancel"
            onClick={on_cancel}
          >
            Cancel
          </Button>
          <Button
            data-testid="renewal-confirm-submit"
            onClick={on_confirm}
          >
            Change renewal month
          </Button>
        </>
      }
    >
      <div data-testid="renewal-confirm-modal">
        <p className="text-sm text-text-secondary">
          Move the opportunity's renewal from{" "}
          <strong className="text-text-primary">
            {MONTH_LABEL(from.renewal_year, from.renewal_month)}
          </strong>{" "}
          to{" "}
          <strong className="text-text-primary">
            {MONTH_LABEL(to.renewal_year, to.renewal_month)}
          </strong>?
          {" "}This affects cadence scheduling and the Renewal Board view.
        </p>
      </div>
    </Modal>
  );
}
